import {
  useCatalogInfo,
  usePriceContextHeader,
  usePriceInfoContextHeader,
  useRestApi
} from 'app/common/hooks';
import { useEffect, useRef } from 'react';
import { isEmpty, merge } from 'lodash';
import { logError } from 'app/common/utils/ApiErrorUtils';
import * as RequestStatus from 'app/common/hooks/useRestApi/RequestStatus';
import { Environment } from 'app/common/services';

/**
 * Fetches pricing for the given products.
 *
 * @return {{priceInfoResponse: {}, isLoaded: boolean}} the price info keyed by
 *   product id, and whether the pricing request has completed
 */
function useProductPricingApi(items) {
  const priceContextHeader = usePriceContextHeader();
  const priceInfoContextHeader = usePriceInfoContextHeader();
  const {
    productInfo: { priceUri, fetchUrl }
  } = useCatalogInfo();
  const productPriceUri = `${fetchUrl}${priceUri}`;

  const {
    exception,
    error,
    response,
    requestStatus,
    sendCallback: fetchPricing
  } = useRestApi(productPriceUri, {}, false);

  const pricingTimeout = getPricingTimeout();

  const itemIds = Array.isArray(items)
    ? items.map(item => item.id).join(',')
    : '';
  const requestedItemIds = useRef(null);

  useEffect(() => {
    if (requestStatus !== RequestStatus.PENDING || isEmpty(itemIds)) {
      return;
    }

    const config = merge({}, priceContextHeader, priceInfoContextHeader, {
      params: { productIds: itemIds },
      timeout: pricingTimeout
    });

    requestedItemIds.current = itemIds;
    fetchPricing(config);
  }, [
    itemIds,
    requestStatus,
    fetchPricing,
    priceContextHeader,
    priceInfoContextHeader,
    pricingTimeout
  ]);

  const defaultPrices = {};
  if (error) {
    const defaultPricesArr = Array.isArray(items)
      ? items.map(item => ({
          [item.id]: {
            productId: item.id,
            priceInfo: item.priceInfo
          }
        }))
      : [];

    for (let obj of defaultPricesArr) {
      merge(defaultPrices, obj);
    }

    logError({
      ...exception,
      when: 'fetching prices for product'
    });
  }

  return {
    priceInfoResponse: isEmpty(response) ? defaultPrices : response,
    // only loaded if the completed request was for the current items
    isLoaded:
      requestStatus === RequestStatus.COMPLETE &&
      requestedItemIds.current === itemIds
  };
}

function getPricingTimeout() {
  return Environment.get('pricing.fetch.timeout', 6000);
}

export default useProductPricingApi;
